import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export interface PromotionPayload {
    discount: number;
    message: string;
    finishDays: number;
    subscribeCounts: number;
    subscribeDays: number;
    type: ('all' | 'expired')[];
}
export interface PromoCampaign {
    id: number;
    message: string;
    rawMessage: string;
    hasRelatedPromo: boolean;
    price: number;
    type: 'all' | 'expired' | string; // extend as needed
    canClaim: boolean;
    claimsCount: number;
    subscribeCounts: number;
    subscribeDays: number;
    createdAt: Date; // ISO date string
    finishedAt: Date | null; // sometimes promos may end early or be null
    isFinished: boolean;
}

export interface AutoPromotionSettings {
    onlyfansId: number; // BigInt mapped to JavaScript number
    type: string;
    discount: number;
    message: string;
    finishDays: number;
    subscribeCounts: number;
    subscribeDays: number;
    repeat: boolean;
}

const useSextforceMetricsAutoPromotions = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const dialog = useDialog();
    const axios = useAxios();
    const { data: subscriber } = useSubscriber();

    const [isCreatePromoLoading, setIsCreatePromoLoading] = useState<boolean>(false);
    const [isPromoSettingsLoading, setIsPromoSettingsLoading] = useState<boolean>(false);

    const createPromotion = async (request: PromotionPayload, repeat: boolean): Promise<PromoCampaign | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber && subscriber.isLoggedIn) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/promotions/${repeat}`;

            setIsCreatePromoLoading(true);

            const data = await axios
                .post(query, request)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    } else {
                        return Promise.reject(response.data);
                    }
                })
                .then(newPromo => {
                    setIsCreatePromoLoading(false);

                    return newPromo as PromoCampaign;
                })
                .catch(error => {
                    console.error(error);

                    setIsCreatePromoLoading(false);

                    handleHttpError(error, dialog);

                    return null;
                });

            return data;
        }

        return null;
    };

    const getCurrentNewSubscriberPromotionSettings = async (): Promise<AutoPromotionSettings | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber && subscriber.isLoggedIn) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/promotions/settings/new`;

            setIsPromoSettingsLoading(true);

            const data = await axios
                .get(query)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    } else {
                        return Promise.reject(response.data);
                    }
                })
                .then(newPromo => {
                    setIsPromoSettingsLoading(false);

                    return newPromo as AutoPromotionSettings;
                })
                .catch(error => {
                    console.error(error);

                    setIsPromoSettingsLoading(false);

                    handleHttpError(error, dialog);

                    return null;
                });

            return data;
        }

        return null;
    };

    const getCurrentExpiredSubscriberPromotionSettings = async (): Promise<AutoPromotionSettings | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber && subscriber.isLoggedIn) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/promotions/settings/expired`;

            setIsPromoSettingsLoading(true);

            const data = await axios
                .get(query)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    } else {
                        return Promise.reject(response.data);
                    }
                })
                .then(newPromo => {
                    setIsPromoSettingsLoading(false);

                    return newPromo as AutoPromotionSettings;
                })
                .catch(error => {
                    console.error(error);

                    setIsPromoSettingsLoading(false);

                    handleHttpError(error, dialog);

                    return null;
                });

            return data;
        }

        return null;
    };

    const setAutoPromoSettingRepeatOption = async (type: string, repeat: boolean): Promise<AutoPromotionSettings | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/promotions/updateRepeat/${type}/${repeat}`;

            const data = await axios
                .post(query)
                .then(response => {
                    if (response.status === 200) {
                        return response.data;
                    } else {
                        return Promise.reject(response.data);
                    }
                })
                .then(newPromo => {
                    return newPromo as AutoPromotionSettings;
                })
                .catch(error => {
                    console.error(error);

                    handleHttpError(error, dialog);

                    return null;
                });

            return data;
        }

        return null;
    };

    return {
        createPromotion,
        getCurrentNewSubscriberPromotionSettings,
        getCurrentExpiredSubscriberPromotionSettings,
        setAutoPromoSettingRepeatOption,
        isCreatePromoLoading,
        isPromoSettingsLoading,
    };
};

export default useSextforceMetricsAutoPromotions;
